import { useCallback } from "react";
import type { Message } from "@/types/chat";
import { sendChatMessage } from "@/api/message/sendmessage";
import { createSession } from "@/api/createsession";
import { useSessionStore } from "@/store/SessionStore";
import { useDeepThinkStore } from "@/store/DeepThinkStore";
import { useChatIdStore } from "@/store/ChatIdStore";
import { useChatStore } from "@/store/ChatStore";
import { useAgentStore } from "@/store/AgentStore";
import { extractStreamId } from "@/api/message/common/MessageParser";

function createChunkBatcher(
  onFlush: (chunk: string, streamId?: number) => void,
) {
  let pending = "";
  let rafId: number | null = null;
  let pendingStreamId: number | undefined;

  return {
    push(chunk: string, streamId?: number) {
      pending += chunk;
      if (streamId) pendingStreamId = streamId;
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        const flushed = pending;
        const streamId = pendingStreamId;
        pending = "";
        pendingStreamId = undefined;
        rafId = null;
        if (flushed) onFlush(flushed, streamId);
      });
    },
    flush() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (pending) {
        const flushed = pending;
        const streamId = pendingStreamId;
        pending = "";
        pendingStreamId = undefined;
        onFlush(flushed, streamId);
      }
    },
  };
}

interface UseChatOptions {
  initialMessages?: Message[];
  onFinish?: () => void;
  useHistory?: number;
}

interface UseChatResult {
  messages: Message[];
  input: string;
  setInput: (input: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  stop: () => void;
  append: (
    message: { content: string; role: "user" | "assistant" },
    options?: { enableTypewriter?: boolean },
  ) => Promise<void>;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  error?: Error;
  streamingMessageId?: string;
  setStreamingMessageId: (id: string | undefined) => void;
}

export function useChat(options: UseChatOptions = {}): UseChatResult {
  const { onFinish, useHistory = 1 } = options;
  const { sessionId, setSessionId } = useSessionStore();
  const { deepThink } = useDeepThinkStore();
  const { startChatId } = useChatIdStore();

  const {
    messages,
    input,
    isLoading,
    streamingMessageId,
    setMessages,
    setInput,
    setIsLoading,
    setStreamingMessageId,
  } = useChatStore();

  const sendMessage = useCallback(
    async (content: string, files: any[] = [], quote?: string) => {
      const fileUrls = files
        .map((file) => (typeof file === "string" ? file : file?.url))
        .filter((url): url is string => Boolean(url));

      if ((!content.trim() && fileUrls.length === 0) || isLoading) return;

      setIsLoading(true);

      const userMessage: Message = {
        id: `msg-${Date.now()}`,
        role: "user",
        content: content,
        createdAt: new Date(),
        files: fileUrls.length > 0 ? fileUrls : undefined,
        quote: quote,
      };

      setMessages((prev) => [...prev, userMessage]);

      let currentSessionId = sessionId;
      if (!currentSessionId) {
        try {
          const response = await createSession({ title: content.slice(0, 50) });
          if (response.data?.id) {
            currentSessionId = response.data.id.toString();
            setSessionId(currentSessionId);
            const { currentAgent, setSessionAgent } = useAgentStore.getState();
            if (currentAgent && response.data?.id) {
              setSessionAgent(response.data.id, currentAgent.id);
            }
          } else {
            throw new Error("未能获取 session_id");
          }
        } catch (err) {
          console.error(err);
          setIsLoading(false);
          return;
        }
      }

      const msgId = `msg-${Date.now() + 1}`;
      setStreamingMessageId(msgId);
      setMessages((prev) => [
        ...prev,
        {
          id: msgId,
          role: "assistant",
          content: "",
          createdAt: new Date(),
        },
      ]);

      const controller = new AbortController();
      let batcher: ReturnType<typeof createChunkBatcher> | undefined;

      try {
        let extractedStreamId: number | undefined = undefined;
        const { currentAgent, agentDismissed } = useAgentStore.getState();
        const agentId =
          !agentDismissed && currentAgent ? currentAgent.id : undefined;

        batcher = createChunkBatcher((chunk, streamId) => {
          if (streamId) extractedStreamId = streamId;
          setMessages((prev) => {
            const lastIndex = prev.length - 1;
            if (lastIndex >= 0 && prev[lastIndex].id === msgId) {
              const updated = [...prev];
              updated[lastIndex] = {
                ...prev[lastIndex],
                content: prev[lastIndex].content + chunk,
                streamId: extractedStreamId || prev[lastIndex].streamId,
              };
              return updated;
            }
            return prev;
          });
        });

        await sendChatMessage(
          content,
          currentSessionId,
          useHistory,
          (chunk: string) => {
            const streamIdData = extractStreamId(chunk);
            batcher!.push(chunk, streamIdData?.id);
          },
          controller.signal,
          fileUrls,
          deepThink,
          startChatId,
          quote
            ? quote.length > 25
              ? quote.slice(0, 25) + "..."
              : quote
            : undefined,
          agentId,
        );
        onFinish?.();
      } catch (err) {
        console.error("发送消息失败:", err);
      } finally {
        batcher?.flush();
        setIsLoading(false);
        setStreamingMessageId(undefined);
      }
    },
    [
      isLoading,
      sessionId,
      setSessionId,
      useHistory,
      onFinish,
      deepThink,
      startChatId,
      setMessages,
      setIsLoading,
      setStreamingMessageId,
    ],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const customEvent = e as any;
      const files = customEvent.files || customEvent.attachments || [];
      const quote = customEvent.quote;
      const fileUrls = files
        .map((file: string | { url?: string }) =>
          typeof file === "string" ? file : file?.url,
        )
        .filter((url: string | undefined): url is string => Boolean(url));

      if (!input.trim() && fileUrls.length === 0) return;

      const currentInput = input.trim();
      setInput("");

      await sendMessage(currentInput, files, quote);
    },
    [input, sendMessage, setInput],
  );

  const append = useCallback(
    async (
      message: { content: string; role: "user" | "assistant" },
      options?: { enableTypewriter?: boolean },
    ) => {
      if (message.role === "assistant" && !options?.enableTypewriter) {
        setMessages((prev) => [
          ...prev,
          {
            id: `msg-${Date.now()}`,
            ...message,
            createdAt: new Date(),
          },
        ]);
        return;
      }
      if (message.role === "user") {
        await sendMessage(message.content);
      }
    },
    [sendMessage, setMessages],
  );

  const stop = useCallback(() => {
    setIsLoading(false);
    setStreamingMessageId(undefined);
  }, [setIsLoading, setStreamingMessageId]);

  return {
    messages,
    input,
    setInput,
    handleSubmit,
    isLoading,
    stop,
    append,
    setMessages,
    streamingMessageId,
    setStreamingMessageId,
  };
}
